import React from 'react';
import Layout from 'components/Common/Layout';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import * as api from 'utils/api';
import { FloorPlanStatus } from 'auto-design-common';
import useTranslation from 'hooks/useTranslation';
import FloorPlanStatusBadge from 'components/Common/FloorPlanStatusBadge';
import { Button } from 'react-bootstrap';
import styles from './FloorPlans.module.scss';

export default function FloorPlans() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['floorPlans'],
    queryFn: ({ pageParam }) => api.getFloorPlans({ limit: 20, cursor: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.page?.hasMore) return lastPage.page.nextCursor;
      return undefined;
    },
  });
  const { tFloorPlanPackageShort, tFloorPlans } = useTranslation();
  const history = useHistory();

  const items = React.useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(p => p.items || []);
  }, [data]);

  return (
    <Layout>
      <div className={styles.title}>
        {tFloorPlans('title')}
        <Button onClick={() => {
          history.push('/create-floor-plan');
        }}
        >
          {tFloorPlans('addFloorPlan')}
        </Button>
      </div>

      <div className={styles.listWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                {tFloorPlans('status')}
              </th>
              <th>
                {tFloorPlans('name')}
              </th>
              <th>
                {tFloorPlans('floors')}
              </th>
              <th>
                {tFloorPlans('packages')}
              </th>
              <th>
                {tFloorPlans('createdAt')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map(f => (
              <tr
                key={f.id}
                onClick={() => {
                  if ([FloorPlanStatus.READY, FloorPlanStatus.EXTRA_COST_APPROVAL, FloorPlanStatus.CREATED, FloorPlanStatus.REQUEST_FILES, FloorPlanStatus.CHANGE_REQUESTED, FloorPlanStatus.NEEDS_INFORMATION].includes(f.status)) {
                    history.push(`/floor-plans/${f.id}`);
                  }
                }}
              >
                <td>
                  <FloorPlanStatusBadge status={f.status} />
                </td>
                <td>{f.name}</td>
                <td>{f.metadata.floors}</td>
                <td>
                  {f.packages.map(p => (
                    <div className={styles.package} key={p}>
                      {tFloorPlanPackageShort(p)}
                    </div>
                  ))}
                </td>
                <td>{moment.utc(f.createdAt).local().format('MM-DD-yyyy HH:mm')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasNextPage && (
          <div className={styles.loadMoreWrapper}>
            <Button
              variant="secondary"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? tFloorPlans('loading') : tFloorPlans('loadMore')}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
